import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { Observable, map, tap, zip } from "rxjs";
import { Ng1StateProxy } from "@gtmhub/core/routing";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import { HomeService } from "@gtmhub/home/services/home";
import { InsightboardService } from "@gtmhub/insightboards";
import { ICollection } from "@gtmhub/models";
import { IReport } from "@gtmhub/reporting/models";
import { INavItem } from "@webapp/navigation/models/nav-items-list.models";
import { SubNavOption } from "@webapp/navigation/models/sub-nav-options.models";
import NavigationItemsMediator from "@webapp/navigation/services/uxcustomization/navigation-items.mediator.service";
import { SuggestedFacade } from "@webapp/navigation/services/uxcustomization/suggested/suggested.facade.service";
import { PermissionsFacade } from "@webapp/permissions/services/permissions-facade.service";
import { BaseSubNav } from "../services/base-sub-nav";

@Component({
  selector: "gh-reports-sub-navigation",
  templateUrl: "./reports-sub-navigation.component.html",
})
export class ReportsSubNavigationComponent extends BaseSubNav implements OnInit {
  @Output() private titleChange = new EventEmitter<string>();
  public options: SubNavOption[] = [
    // { label: "My Reports", state: "gtmhub.lists" },
    { label: "All Reports", state: "gtmhub.lists" },
  ];
  public customReportOptions: SubNavOption[] = [];
  public hideListsFeatureFlag: boolean;
  public showInsightReports$: Observable<boolean>;
  public suggested$: Observable<ICollection<INavItem>>;

  private sectionTitle = "Reports";

  constructor(
    private homeService: HomeService,
    private insightboardService: InsightboardService,
    private featureToggleService: FeatureToggleService,
    private suggestedFacade: SuggestedFacade,
    private ng1StateProxy: Ng1StateProxy,
    public permissionsFacade: PermissionsFacade,
    protected navigationItemsMediator: NavigationItemsMediator
  ) {
    super();
    const performanceReportId = this.getPerformanceReportId();
    const processReportId = this.getProcessReportId();
    if (performanceReportId && processReportId) {
      this.customReportOptions.push(this.buildPerformanceReportOption(performanceReportId));
      this.customReportOptions.push(this.buildProcessReportOption(processReportId));
    } else {
      this.insightboardService.getReports().then((reports: IReport[]) => {
        this.homeService.cachedReports = reports;
        const performanceReport = this.homeService.cachedReports.find((report) => report.name.toLowerCase() === "performance report");
        const processReport = this.homeService.cachedReports.find((report) => report.name.toLowerCase() === "process report");
        this.customReportOptions.push(this.buildPerformanceReportOption(performanceReport.id));
        this.customReportOptions.push(this.buildProcessReportOption(processReport.id));
      });
    }
    this.setupEntityTypes(["list"], "lists");

    this.suggested$ = this.suggestedFacade.getSuggestedLists$();
  }

  public ngOnInit(): void {
    this.showInsightReports$ = zip(this.permissionsFacade.hasPermission$("ManageData"), this.permissionsFacade.hasPermission$("AccessReportsAndDataSourceFilters"))
      .pipe(
        map(([canManageData, canAccessReportsAndDataSourceFilters]) => {
          return canManageData || canAccessReportsAndDataSourceFilters;
        })
      )
      .pipe(
        tap((willShowReports) => {
          if (!willShowReports) {
            this.ng1StateProxy.go("gtmhub.lists");
          }
        })
      );
    this.featureToggleService.isFeatureAvailable("hide-lists").then((enabled) => {
      this.hideListsFeatureFlag = enabled;
      if (!enabled) {
        super.ngOnInit();
      }
    });
    this.titleChange.emit(this.sectionTitle);
  }

  private getReportId(reportName: string): string {
    if (!this.homeService.cachedReports) return null;
    const performanceReport = this.homeService.cachedReports.find((report) => report.name.toLowerCase() === reportName);
    if (!performanceReport) return null;
    return performanceReport.id || null;
  }

  protected itemMarkedAsFavorite(item: INavItem): void {
    this.suggestedFacade.removeSuggestedItem(item.id, "lists");
  }

  private getPerformanceReportId(): string {
    return this.getReportId("performance report");
  }

  private getProcessReportId(): string {
    return this.getReportId("process report");
  }
  private buildPerformanceReportOption(reportId: string): SubNavOption {
    return { label: "Performance", state: "gtmhub.insightboard", icon: "performance", stateOptions: { dashboardId: reportId } };
  }

  private buildProcessReportOption(reportId: string): SubNavOption {
    return { label: "Process", state: "gtmhub.insightboard", icon: "process", stateOptions: { dashboardId: reportId } };
  }
}
